import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, setAuthToken, getAuthToken } from '@/lib/api';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load /me on app mount if there's a stored token
  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      setLoading(false);
      return;
    }
    api.get('/auth/me')
      .then((r) => setUser(r.data))
      .catch(() => {
        setAuthToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post('/auth/login', { email, password });
    setAuthToken(r.data.access_token);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(() => {
    setAuthToken(null);
    setUser(null);
    window.location.href = '/login';
  }, []);

  const refreshMe = useCallback(async () => {
    const r = await api.get('/auth/me');
    setUser(r.data);
    return r.data;
  }, []);

  const hasRole = useCallback((rolesAllowed) => {
    if (!user) return false;
    if (!rolesAllowed || rolesAllowed.length === 0) return true;
    return rolesAllowed.includes(user.role);
  }, [user]);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refreshMe, hasRole, isAuthenticated: !!user }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// Mapping role -> allowed paths. Used by sidebar filter and ProtectedRoute.
export const ROLE_PERMISSIONS = {
  cocina: ['/kds'],
  caixa: ['/dashboard', '/pos', '/floor-plan', '/reservations', '/kds', '/menu', '/recipes', '/inventory', '/suppliers'],
  admin_empresa: ['/dashboard', '/pos', '/floor-plan', '/reservations', '/kds', '/menu', '/recipes', '/inventory', '/suppliers', '/expenses', '/income', '/invoices', '/reports', '/pgc', '/settings', '/locations'],
  admin_ferramenta: ['/dashboard', '/pos', '/floor-plan', '/reservations', '/kds', '/menu', '/recipes', '/inventory', '/suppliers', '/expenses', '/income', '/invoices', '/reports', '/pgc', '/settings', '/locations', '/users'],
};

export function pathAllowed(role, path) {
  if (!role) return false;
  const allowed = ROLE_PERMISSIONS[role] || [];
  return allowed.some((p) => path === p || path.startsWith(`${p}/`));
}

export const ROLE_LABELS = {
  es: {
    cocina: 'Cocina',
    caixa: 'Caja / Camarero',
    admin_empresa: 'Admin del restaurante',
    admin_ferramenta: 'Super-admin (herramienta)',
  },
  pt: {
    cocina: 'Cozinha',
    caixa: 'Caixa / Garçom',
    admin_empresa: 'Admin do restaurante',
    admin_ferramenta: 'Super-admin (ferramenta)',
  },
};
